// =====================================================
// GRÁFICO NDVI POR LOTE - COPERNICUS DATA SPACE
// Adaptación de GraficoNdvi para un polígono específico
// src/pages/Agro/Establecimiento/GraficoNdviLote.js
// =====================================================

import React, { useState, useEffect } from "react"
import { getErrorMessage } from "helpers/error_helpers"
import {
  Card,
  CardBody,
  CardHeader,
  Row,
  Col,
  Badge,
  Spinner,
  Alert,
  Button,
  ButtonGroup,
  Progress,
} from "reactstrap"
import {
  ComposedChart,
  Line,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
} from "recharts"
import dayjs from "dayjs"

import { getNdviPoligono, getSerieNdviPoligono } from "api/agro"

// Clasificación NDVI con colores
const getNdviInfo = ndvi => {
  if (ndvi === null || ndvi === undefined)
    return { label: "Sin datos", color: "#6c757d", bg: "secondary" }
  if (ndvi >= 0.6)
    return { label: "Excelente", color: "#228B22", bg: "success" }
  if (ndvi >= 0.4) return { label: "Bueno", color: "#32CD32", bg: "info" }
  if (ndvi >= 0.2) return { label: "Regular", color: "#FFD700", bg: "warning" }
  return { label: "Malo", color: "#DC143C", bg: "danger" }
}

// Tooltip personalizado para el gráfico
const CustomTooltip = ({ active, payload }) => {
  if (!active || !payload || !payload.length) return null

  const data = payload[0].payload
  const info = getNdviInfo(data.ndvi)

  return (
    <div className="bg-white p-2 border rounded shadow-sm">
      <p className="mb-1 fw-bold">{data.fechaCompleta}</p>
      <p className="mb-1">
        NDVI Promedio:{" "}
        <strong style={{ color: info.color }}>{data.ndvi?.toFixed(3)}</strong>
      </p>
      {data.ndviMin !== undefined && (
        <p className="mb-1 small text-muted">
          Rango: {data.ndviMin?.toFixed(2)} - {data.ndviMax?.toFixed(2)}
        </p>
      )}
      <Badge color={info.bg}>{info.label}</Badge>
    </div>
  )
}

const GraficoNdviLote = ({ idPoligono, poligono }) => {
  const [loading, setLoading] = useState(true)
  const [loadingSerie, setLoadingSerie] = useState(false)
  const [ndviActual, setNdviActual] = useState(null)
  const [serieNdvi, setSerieNdvi] = useState([])
  const [periodoMeses, setPeriodoMeses] = useState(6)
  const [error, setError] = useState(null)
  const [errorSerie, setErrorSerie] = useState(null)

  useEffect(() => {
    if (idPoligono) {
      loadNdviActual()
    }
  }, [idPoligono])

  useEffect(() => {
    if (idPoligono) {
      loadSerieNdvi()
    }
  }, [idPoligono, periodoMeses])

  const loadNdviActual = async () => {
    setLoading(true)
    setError(null)
    try {
      const data = await getNdviPoligono(idPoligono)
      setNdviActual(data)
    } catch (err) {
      console.error("Error cargando NDVI actual:", err)
      // BDSJEA-460: message del backend (p. ej. lote sin geometría) en vez de un texto genérico
      setError(
        getErrorMessage(err, {
          fallback: "Error al cargar datos de vegetación actuales",
        }),
      )
    } finally {
      setLoading(false)
    }
  }

  const loadSerieNdvi = async () => {
    setLoadingSerie(true)
    setErrorSerie(null)
    try {
      const hasta = dayjs().format("YYYY-MM-DD")
      const desde = dayjs()
        .subtract(periodoMeses, "months")
        .format("YYYY-MM-DD")

      const data = await getSerieNdviPoligono(idPoligono, desde, hasta)
      setSerieNdvi(data || [])
    } catch (err) {
      console.error("Error cargando serie NDVI:", err)
      setSerieNdvi([])
      setErrorSerie(
        getErrorMessage(err, {
          fallback: "No se pudo cargar la evolución histórica del NDVI.",
        }),
      )
    } finally {
      setLoadingSerie(false)
    }
  }

  // Preparar datos para el gráfico
  const chartData = serieNdvi
    .map(d => ({
      fecha: dayjs(d.fechaCaptura).format("DD/MM"),
      fechaCompleta: dayjs(d.fechaCaptura).format("DD/MM/YYYY"),
      fechaSort: d.fechaCaptura,
      ndvi: d.ndviPromedio,
      ndviMin: d.ndviMinimo,
      ndviMax: d.ndviMaximo,
      desvio: d.ndviDesvio,
    }))
    .sort((a, b) => new Date(a.fechaSort) - new Date(b.fechaSort))

  // Calcular estadísticas
  const calcularEstadisticas = () => {
    if (chartData.length === 0) return null

    const valores = chartData.map(d => d.ndvi).filter(v => v != null)
    if (valores.length === 0) return null

    const promedio = valores.reduce((a, b) => a + b, 0) / valores.length
    const max = Math.max(...valores)
    const min = Math.min(...valores)
    const tendencia =
      valores.length > 1
        ? ((valores[valores.length - 1] - valores[0]) / valores[0]) * 100
        : 0

    return { promedio, max, min, tendencia, puntos: valores.length }
  }

  const stats = calcularEstadisticas()
  const infoActual = ndviActual ? getNdviInfo(ndviActual.ndviPromedio) : null

  if (loading) {
    return (
      <Card>
        <CardBody className="text-center py-5">
          <Spinner color="primary" />
          <p className="mt-2 text-muted">Cargando datos de vegetación...</p>
        </CardBody>
      </Card>
    )
  }

  if (error) {
    return (
      <Card>
        <CardBody>
          <Alert color="warning">{error}</Alert>
        </CardBody>
      </Card>
    )
  }

  return (
    <Card>
      <CardHeader className="d-flex justify-content-between align-items-center">
        <div>
          <h5 className="mb-0">
            <i className="mdi mdi-leaf me-2"></i>
            Índice de Vegetación (NDVI) -{" "}
            {poligono?.nombreLote || `Lote ${idPoligono}`}
          </h5>
          {ndviActual?.fuente && (
            <small className="text-muted">Fuente: {ndviActual.fuente}</small>
          )}
        </div>
        <ButtonGroup size="sm">
          <Button
            color={periodoMeses === 3 ? "primary" : "outline-primary"}
            onClick={() => setPeriodoMeses(3)}
          >
            3 meses
          </Button>
          <Button
            color={periodoMeses === 6 ? "primary" : "outline-primary"}
            onClick={() => setPeriodoMeses(6)}
          >
            6 meses
          </Button>
          <Button
            color={periodoMeses === 12 ? "primary" : "outline-primary"}
            onClick={() => setPeriodoMeses(12)}
          >
            1 año
          </Button>
        </ButtonGroup>
      </CardHeader>

      <CardBody>
        {/* Estado actual del NDVI */}
        {ndviActual && (
          <Row className="mb-4">
            <Col md={3}>
              <div className="text-center p-3 border rounded">
                <h6 className="text-muted mb-2">NDVI Actual</h6>
                <h2 style={{ color: infoActual?.color }}>
                  {ndviActual.ndviPromedio?.toFixed(2) || "-"}
                </h2>
                <Badge color={infoActual?.bg} className="px-3">
                  {ndviActual.condicionVegetacion || infoActual?.label}
                </Badge>
                <p className="small text-muted mt-2 mb-0">
                  {ndviActual.fechaCaptura &&
                    dayjs(ndviActual.fechaCaptura).format("DD/MM/YYYY")}
                </p>
              </div>
            </Col>

            <Col md={3}>
              <div className="text-center p-3 border rounded">
                <h6 className="text-muted mb-2">Score Riesgo</h6>
                <h2
                  className={
                    ndviActual.scoreRiesgo >= 70
                      ? "text-success"
                      : ndviActual.scoreRiesgo >= 40
                        ? "text-warning"
                        : "text-danger"
                  }
                >
                  {ndviActual.scoreRiesgo || "-"}
                </h2>
                <Progress
                  value={ndviActual.scoreRiesgo || 0}
                  color={
                    ndviActual.scoreRiesgo >= 70
                      ? "success"
                      : ndviActual.scoreRiesgo >= 40
                        ? "warning"
                        : "danger"
                  }
                  className="mt-2"
                />
              </div>
            </Col>

            <Col md={3}>
              <div className="text-center p-3 border rounded">
                <h6 className="text-muted mb-2">Rango NDVI</h6>
                <p className="mb-0">
                  <span className="text-danger">
                    {ndviActual.ndviMinimo?.toFixed(2) || "-"}
                  </span>
                  {" - "}
                  <span className="text-success">
                    {ndviActual.ndviMaximo?.toFixed(2) || "-"}
                  </span>
                </p>
                <small className="text-muted">
                  Desv: ±{ndviActual.ndviDesvio?.toFixed(3) || "-"}
                </small>
              </div>
            </Col>

            <Col md={3}>
              <div className="text-center p-3 border rounded">
                <h6 className="text-muted mb-2">Cobertura</h6>
                {ndviActual.pixelesValidos && ndviActual.pixelesTotales ? (
                  <>
                    <p className="mb-0">
                      {(
                        (ndviActual.pixelesValidos /
                          ndviActual.pixelesTotales) *
                        100
                      ).toFixed(0)}
                      %
                    </p>
                    <small className="text-muted">
                      {ndviActual.pixelesValidos?.toLocaleString()} /{" "}
                      {ndviActual.pixelesTotales?.toLocaleString()} px
                    </small>
                  </>
                ) : (
                  <p className="text-muted mb-0">-</p>
                )}
              </div>
            </Col>
          </Row>
        )}

        {/* Descripción del estado */}
        {ndviActual?.descripcionEstado && (
          <Alert
            color={
              infoActual?.bg === "success" || infoActual?.bg === "info"
                ? "success"
                : infoActual?.bg === "warning"
                  ? "warning"
                  : "danger"
            }
            className="mb-4"
          >
            <i className="mdi mdi-information-outline me-2"></i>
            {ndviActual.descripcionEstado}
          </Alert>
        )}

        {/* Gráfico de serie temporal */}
        <h6 className="mb-3">
          Evolución Histórica
          {loadingSerie && <Spinner size="sm" className="ms-2" />}
        </h6>

        {errorSerie && <Alert color="warning">{errorSerie}</Alert>}

        {chartData.length > 0 ? (
          <>
            <div style={{ width: "100%", height: 300 }}>
              <ResponsiveContainer>
                <ComposedChart
                  data={chartData}
                  margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
                >
                  <defs>
                    <linearGradient
                      id="ndviGradientLote"
                      x1="0"
                      y1="0"
                      x2="0"
                      y2="1"
                    >
                      <stop offset="5%" stopColor="#28a745" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#28a745" stopOpacity={0} />
                    </linearGradient>
                  </defs>

                  <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                  <XAxis
                    dataKey="fecha"
                    tick={{ fontSize: 11 }}
                    interval="preserveStartEnd"
                  />
                  <YAxis
                    domain={[0, 1]}
                    ticks={[0, 0.2, 0.4, 0.6, 0.8, 1.0]}
                    tick={{ fontSize: 11 }}
                  />
                  <Tooltip content={<CustomTooltip />} />

                  <ReferenceLine
                    y={0.6}
                    stroke="#228B22"
                    strokeDasharray="5 5"
                    label={{
                      value: "Excelente",
                      fontSize: 10,
                      fill: "#228B22",
                    }}
                  />
                  <ReferenceLine
                    y={0.4}
                    stroke="#32CD32"
                    strokeDasharray="5 5"
                    label={{ value: "Bueno", fontSize: 10, fill: "#32CD32" }}
                  />
                  <ReferenceLine
                    y={0.2}
                    stroke="#FFD700"
                    strokeDasharray="5 5"
                    label={{ value: "Regular", fontSize: 10, fill: "#FFD700" }}
                  />

                  <Area
                    type="monotone"
                    dataKey="ndviMax"
                    stroke="none"
                    fill="url(#ndviGradientLote)"
                  />

                  <Line
                    type="monotone"
                    dataKey="ndvi"
                    stroke="#28a745"
                    strokeWidth={2}
                    dot={{ r: 4, fill: "#28a745" }}
                    activeDot={{ r: 6 }}
                    name="NDVI Promedio"
                  />
                </ComposedChart>
              </ResponsiveContainer>
            </div>

            {/* Estadísticas del período */}
            {stats && (
              <Row className="mt-3 text-center">
                <Col>
                  <small className="text-muted">Promedio período</small>
                  <p className="mb-0 fw-bold">{stats.promedio.toFixed(3)}</p>
                </Col>
                <Col>
                  <small className="text-muted">Máximo</small>
                  <p className="mb-0 fw-bold text-success">
                    {stats.max.toFixed(3)}
                  </p>
                </Col>
                <Col>
                  <small className="text-muted">Mínimo</small>
                  <p className="mb-0 fw-bold text-danger">
                    {stats.min.toFixed(3)}
                  </p>
                </Col>
                <Col>
                  <small className="text-muted">Tendencia</small>
                  <p
                    className={`mb-0 fw-bold ${stats.tendencia >= 0 ? "text-success" : "text-danger"}`}
                  >
                    {stats.tendencia >= 0 ? "+" : ""}
                    {stats.tendencia.toFixed(1)}%
                  </p>
                </Col>
                <Col>
                  <small className="text-muted">Observaciones</small>
                  <p className="mb-0 fw-bold">{stats.puntos}</p>
                </Col>
              </Row>
            )}
          </>
        ) : (
          <Alert color="info">
            <i className="mdi mdi-information-outline me-2"></i>
            No hay datos históricos disponibles para el período seleccionado.
          </Alert>
        )}

        {/* Leyenda de clasificación */}
        <div className="mt-4 pt-3 border-top">
          <span
            className="text-muted d-block mb-2 font-size-14"
            data-testid="leyenda-ndvi-titulo"
          >
            Clasificación NDVI:
          </span>
          <div
            className="d-flex flex-wrap gap-2"
            data-testid="leyenda-ndvi-clasificacion"
          >
            <Badge color="success" className="px-2 font-size-14">
              ≥0.6 Excelente
            </Badge>
            <Badge color="info" className="px-2 font-size-14">
              0.4-0.6 Bueno
            </Badge>
            <Badge color="warning" className="px-2 font-size-14">
              0.2-0.4 Regular
            </Badge>
            <Badge color="danger" className="px-2 font-size-14">
              &lt;0.2 Malo
            </Badge>
          </div>
        </div>
      </CardBody>
    </Card>
  )
}

export default GraficoNdviLote
